<template>
    <card title="Users">
        <template #header-right>
            <div class="d-flex align-items-center" style="gap: 7px;">
                <b-form-radio-group v-model="showActiveUsers" :options="userViewOptions" button-variant="primary"
                    buttons size="sm" class="text-nowrap" />
                <b-form-checkbox id="online-only" v-model="onlineOnly" switch class="m-0 text-nowrap">
                    Online only
                </b-form-checkbox>
                <label for="config-filter" class="m-0"><i class="fa fa-search" /></label>
                <b-input id="config-filter" v-model="query" type="search" placeholder="Filter Users" />
            </div>
        </template>
        <div class="users-list">
            <card v-for="user in filteredUsers" :key="user.userName" plain class="m-0 cursor-pointer"
                @click.native="selectUser(user)">
                <strong>
                    <span class="online-dot" :class="{ online: presenceFor(user)?.isOnline }" v-b-tooltip.hover
                        :title="presenceFor(user)?.tooltip" />
                    {{ user.toString() }}
                    <template v-if="user.initials">
                        ({{ user.initials }})
                    </template>
                </strong>
                <div>
                    <small>
                        {{ user.userName }}
                    </small>
                </div>
                <div v-if="!user.active" class="text-muted">
                    <small>Deactivated {{ describeDeactivation(user) }}</small>
                </div>
                <div class="mt-2 d-flex flex-wrap" style="gap: 4px;">
                    <b-badge v-for="t in user.teams.toSorted((a, b) => (a.name).localeCompare(b.name))" :key="t.id"
                        variant="primary" class="m-0">
                        {{ t.name }}
                    </b-badge>
                </div>
            </card>
        </div>
        <p v-if="!filteredUsers.length" class="text-muted m-0">
            {{ emptyUsersMessage }}
        </p>
        <b-modal id="user-modal" @ok="handleOk" title="User Management" ok-title="Save User" :hide-footer="!userCanEdit"
            no-fade size="lg">
            <b-row>
                <b-col>
                    <strong>{{ selectedUser?.userName }}</strong>
                </b-col>
                <b-col sm="auto" class="text-right">
                    <!-- Wrapped so the tooltip still fires: a disabled button emits no mouse events. -->
                    <span v-if="canToggleUserActive && selectedUser && selectedUser.active"
                        v-b-tooltip.hover
                        :title="isSelectedUserSelf ? 'You cannot deactivate your own account.' : ''">
                        <b-button size="sm" variant="outline-danger" class="mr-2"
                            :disabled="isSelectedUserSelf" @click="confirmDeactivateUser">
                            <i class="fa fa-user-times" /> Deactivate
                        </b-button>
                    </span>
                    <b-button v-else-if="canToggleUserActive && selectedUser" size="sm" variant="outline-success"
                        class="mr-2" @click="confirmReactivateUser">
                        <i class="fa fa-user-check" /> Reactivate
                    </b-button>
                    <b-button v-if="canEnrollPositiveId" size="sm" variant="outline-primary" class="mr-2"
                        @click="showPositiveIdModal = true">
                        <i class="fas fa-id-badge" /> Positive ID
                    </b-button>
                    <history-modal v-model="selectedUser" />
                </b-col>
            </b-row>

            <b-alert :show="!!selectedUser && !selectedUser.active" variant="warning" class="py-2 mt-2 mb-0">
                This user is inactive: they are hidden from assignment dropdowns. Deactivated
                {{ selectedUser ? describeDeactivation(selectedUser) : "" }}. Signing in reactivates them.
            </b-alert>

            <hr>

            <b-tabs v-if="selectedUser" pills>
                <b-tab title="General">
                    <div class="mt-3">
                        <b-form-row>
                            <b-col>
                                <b-form-group label="First Name" label-for="firstName">
                                    <b-form-input id="firstName" name="firstName" v-model="selectedUser.firstName"
                                        maxlength="40" :disabled="!userCanEdit" />
                                </b-form-group>
                            </b-col>
                            <b-col>
                                <b-form-group label="Last Name" label-for="lastName">
                                    <b-form-input id="lastName" name="lastName" v-model="selectedUser.lastName"
                                        maxlength="40" :disabled="!userCanEdit" />
                                </b-form-group>
                            </b-col>
                        </b-form-row>

                        <b-form-group label="Initials" label-for="initials">
                            <b-form-input id="initials" name="initials" v-model="selectedUser.initials" maxlength="2"
                                :disabled="!userCanEdit" />
                        </b-form-group>

                        <b-form-group label="Remote Worker" label-for="isRemote">
                            <b-form-checkbox id="isRemote" v-model="selectedUser.isRemote" :disabled="!userCanEdit"
                                switch>
                                Grant the Remote Worker permission at login (state restrictions apply).
                            </b-form-checkbox>
                        </b-form-group>
                    </div>
                </b-tab>
                <b-tab title="Compliance">
                    <div class="mt-3">
                        <b-form-group label="NPI Number">
                            <b-form-input v-model="selectedUser.npiNumber" maxlength="10" :disabled="!userCanEdit" />
                        </b-form-group>

                        <hr>

                        <h6>State Licenses</h6>

                        <div class="state-licenses">
                            <card v-for="(l, index) in selectedUser.licenses" plain :key="index" @click.native="editLicense(l, index)">

                                <b-badge :variant="l.active ? 'success' : 'danger'">{{ l.active ? 'Active' : 'Inactive'
                                }}</b-badge>

                                <div>
                                    <strong>
                                        {{ l.state }}:
                                    </strong>
                                    <span style="font-family: monospace;">
                                        {{ l.licenseNumber }}
                                    </span>
                                </div>

                                <div>
                                    <small>
                                        <template v-if="l.startDate && !l.endDate">
                                            Starts On: {{ l.startDate | formatDate("L") }}
                                        </template>
                                        <template v-if="l.endDate && !l.startDate">
                                            Ends On: {{ l.endDate | formatDate("L") }}
                                        </template>
                                        <template v-if="l.startDate && l.endDate">
                                            {{ l.startDate | formatDate("L") }}
                                            -
                                            {{ l.endDate | formatDate("L") }}
                                        </template>

                                    </small>
                                </div>
                            </card>

                            <card plain @click.native="addLicense" class="text-center">
                                <i class="fa fa-plus-circle fa-2x" /><br>
                                Add State License
                            </card>
                        </div>

                        <b-modal :visible="!!selectedUserCompliance" no-fade title="State License"
                            @hidden="selectedUserCompliance = null" @ok="saveLicense">

                            <template v-if="selectedUserCompliance">
                                <b-form-group>
                                    <b-check v-model="selectedUserCompliance.active" switch>Active</b-check>
                                </b-form-group>

                                <fg-select v-model="selectedUserCompliance.state" :options="stateOptions"
                                    label="State" />

                                <b-form-group label="License">
                                    <b-input v-model="selectedUserCompliance.licenseNumber" />
                                </b-form-group>

                                <b-form-row>
                                    <b-col>
                                        <b-form-group label="Start Date">
                                            <el-date-picker v-model="selectedUserCompliance.startDate"
                                                format="MM/dd/yyyy" type="date" today-button reset-button />
                                        </b-form-group>
                                    </b-col>
                                    <b-col>
                                        <b-form-group label="End Date">
                                            <el-date-picker v-model="selectedUserCompliance.endDate" format="MM/dd/yyyy"
                                                type="date" today-button reset-button />
                                        </b-form-group>
                                    </b-col>
                                </b-form-row>
                            </template>
                        </b-modal>
                    </div>
                </b-tab>
                <b-tab title="Teams">
                    <b-table :items="selectedUser.teams" :fields="teamsFields" :foot-clone="userCanEdit">
                        <template #cell(teams)="data">
                            {{ data.item.name }}
                        </template>
                        <template #cell(actions)="data">
                            <b-button size="sm" variant="danger" @click="removeTeam(data.item)"
                                :disabled="!userCanEdit">
                                <i class="fa fa-trash" />
                            </b-button>
                        </template>
                        <template #foot(teams)="">
                            <SearchComponent :key="selectedUser.teams.length" v-model="teamToAdd"
                                search-u-r-l="/User/Teams" prepopulate label=""
                                :results-modifier="teamAddSearchResultFilter" />
                        </template>
                        <template #foot(actions)="">
                            <b-button @click="addTeam" size="sm" variant="success" disabled>
                                <i class="fa fa-plus"></i>
                            </b-button>
                        </template>
                    </b-table>
                </b-tab>
                <b-tab title="Effective Permissions">
                    <div v-if="isWorkingTeamsLoading" class="mt-3 text-center">
                        Calculating Effective Permissions...
                        <b-spinner small />
                    </div>
                    <div v-else-if="!effectivePermissions.length" class="m-5 text-center">
                        <strong>
                            User has no effective permissions
                        </strong>
                        <p>Add this user to teams with defined permissions.</p>
                    </div>
                    <div v-else class="d-flex mt-3 permissions" style="gap: 8px;">
                        <div class="groups">
                            <b-input v-model="permissionFilterQuery" type="search" placeholder="Filter Permissions"
                                class="mb-2" />
                            <b-list-group>
                                <b-list-group-item href="#" :active="!permissionsFilterByGroup"
                                    @click="permissionsFilterByGroup = null"
                                    class="d-flex justify-content-between align-items-center">
                                    All Groups
                                    <b-badge v-if="getAppliedPermissionsCountInGroup()" variant="primary" pill>
                                        {{ getAppliedPermissionsCountInGroup() }}
                                    </b-badge>
                                </b-list-group-item>
                                <b-list-group-item v-for="group in filteredEffectivePermissionsGroups" href="#"
                                    :key="group" :active="permissionsFilterByGroup === group"
                                    @click="permissionsFilterByGroup = group"
                                    class="d-flex justify-content-between align-items-center">
                                    {{ group }}

                                    <b-badge v-if="getAppliedPermissionsCountInGroup(group)" variant="primary" pill
                                        class="ml-1">
                                        {{ getAppliedPermissionsCountInGroup(group) }}
                                    </b-badge>
                                </b-list-group-item>
                            </b-list-group>
                        </div>
                        <div class="flex-fill">
                            <div v-for="group in filteredEffectivePermissionsGroups" :key="group" class="mb-3">
                                <h6>
                                    {{ group }}
                                </h6>
                                <div class="checkmarks">
                                    <div v-for="permission in getEffectivePermissionsForGroup(group)"
                                        :key="permission.id" class="d-flex justify-content-between align-items-start">
                                        {{ permission.name }}

                                        <HelpPopup icon="fa fa-info-circle">
                                            <h6 class="mb-2">
                                                {{ permission.groupName }}
                                                <i class="fa fa-chevron-right" />
                                                {{ permission.name }}
                                            </h6>

                                            <div>
                                                Granted from Teams:
                                                <ul class="mb-0">
                                                    <li v-for="team in getTeamsForPermission(permission)"
                                                        :key="team.id">
                                                        {{ team.name }}
                                                    </li>
                                                </ul>
                                            </div>
                                        </HelpPopup>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab>
                <b-tab title="API">
                    <div class="mt-3">
                        <b-form-group label="InfiniTRAK API Key">
                            <b-form-input v-model="selectedUser.infinitrakApiKey" maxlength="64"
                                :disabled="!userCanEdit" />
                        </b-form-group>
                    </div>
                </b-tab>
            </b-tabs>
        </b-modal>

        <PositiveIdEnrollmentModal v-if="selectedUser" :show.sync="showPositiveIdModal"
            :user-name="selectedUser.userName" :display-name="selectedUser.toString()" />
    </card>

</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import axios from 'axios';
import { User, UserComplianceLicense } from '@/models/User';
import { NotificationOptions } from "@/util/NotificationOptionsPresets"
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import { toRaw } from 'vue';
import { Team } from '@/models/Team';
import SearchComponent from '../SearchComponent.vue';
import { Permission } from '@/models/Permission';
import HelpPopup from '../HelpPopup.vue';
import { ValidValues } from '@/data/ValidValues';
import { DatePicker } from "element-ui";
import PositiveIdEnrollmentModal from '@/components/Common/PositiveIdEnrollmentModal.vue';
import { UserPresenceStatus, UserPresenceView } from '@/models/UserPresence';
import { formatDateAndTime } from '@/util/DateUtils';

@Component({
    name: "UserComponent",
    components: {
        HistoryModal,
        SearchComponent,
        HelpPopup,
        PositiveIdEnrollmentModal,
        [DatePicker.name]: DatePicker,
    }
})
export default class UserComponent extends Vue {
    created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");
        this.fetchAllUsers();
        this.fetchOnlineStatuses();
        this.presencePollHandle = setInterval(() => this.fetchOnlineStatuses(), 30_000);
    }

    beforeDestroy() {
        console.log(`%c Destroying ${this.$options.name}`, "color: red");
        if (this.presencePollHandle) clearInterval(this.presencePollHandle);
    }

    private selectedUser: User | null = null;
    private users: User[] = [];
    private query = "";
    private onlineOnly = false;

    /** True lists users on the active roster, false lists the deactivated ones. */
    private showActiveUsers = true;
    private showPositiveIdModal = false;
    private teamToAdd = new Team();
    private presenceByUserName: Record<string, UserPresenceView> = {};
    private lastPresencePayload = "";
    private presencePollHandle: ReturnType<typeof setInterval> | null = null;

    private workingTeams: Team[] = [];

    protected permissionFilterQuery = "";
    protected permissionsFilterByGroup: string | null = null;

    protected selectedUserCompliance: UserComplianceLicense | null = null;
    protected selectedUserComplianceIndex: number | null = null;

    /**
     * @param notifyOnError pass false when the caller reports the failure itself, so one click
     *   cannot raise two competing toasts.
     * @returns whether `users` now reflects the server. Errors are caught here rather than
     *   rethrown -- created() fires this without awaiting -- so callers that act on the outcome
     *   have to check the return value.
     */
    async fetchAllUsers(notifyOnError = true): Promise<boolean> {
        try {
            // This page is the only caller that asks for inactive users; every other consumer of
            // the endpoint is an assignment picker and wants the active-only default.
            const response = await axios.get<User[]>(`/User`, { params: { includeInactive: true } });
            this.users = response.data.map(u => new User(u)).toSorted((a: User, b: User) => {
                const aName = a.lastName ? a.lastName + a.firstName : a.userName;
                const bName = b.lastName ? b.lastName + b.firstName : b.userName;
                return (aName).localeCompare(bName);
            }).toSorted((a: User, b: User) => {
                return (b.teams.length ? 1 : 0) - (a.teams.length ? 1 : 0);
            });
            return true;
        } catch (err) {
            if (notifyOnError) this.$notification(NotificationOptions.error(err));
            return false;
        }
    }

    async fetchOnlineStatuses() {
        try {
            const response = await axios.get<UserPresenceStatus[]>(`/User/online`);

            // Presence seldom changes between polls, and reassigning would re-render every
            // user card. Skip identical payloads so the common tick costs nothing.
            const payload = JSON.stringify(response.data);
            if (payload === this.lastPresencePayload) return;
            this.lastPresencePayload = payload;

            this.presenceByUserName = Object.fromEntries(
                response.data.map((s): [string, UserPresenceView] => [
                    s.userName.toLowerCase(),
                    {
                        isOnline: s.isOnline,
                        tooltip: s.isOnline
                            ? `Online since ${formatDateAndTime(s.connectedSince)}`
                            : `Last seen ${formatDateAndTime(s.lastSeen)}`,
                    },
                ])
            );
        } catch {
            // Presence is auxiliary; a failed poll just keeps the last-known statuses.
        }
    }

    presenceFor(user: User): UserPresenceView | undefined {
        return this.presenceByUserName[user.userName.toLowerCase()];
    }

    get stateOptions() {
        return ValidValues.addressStates;
    }

    get queryTerms() {
        return this.query.toLowerCase().split(" ");
    }

    /**
     * Users matching the search box and the Online-only switch, with the Active/Inactive split
     * deliberately left out. Both the counts and the visible list derive from this, and the split
     * is the only thing between it and filteredUsers -- so the toggle can no longer read
     * "Active (42)" directly above "No active users found."
     */
    get matchingUsers() {
        return this.users.filter(user => {
            if (this.onlineOnly && !this.presenceFor(user)?.isOnline) return false;

            const userTerms = [
                user.firstName,
                user.lastName,
                user.userName,
                user.initials,
                ...user.teams.map(team => team.name)
            ].join(' ').toLowerCase();

            return this.queryTerms.every(term => userTerms.includes(term));
        });
    }

    get filteredUsers() {
        return this.matchingUsers.filter(user => user.active === this.showActiveUsers);
    }

    get userViewOptions() {
        const active = this.matchingUsers.filter(u => u.active).length;
        const inactive = this.matchingUsers.length - active;
        return [
            { text: active ? `Active (${active})` : "Active", value: true },
            { text: inactive ? `Inactive (${inactive})` : "Inactive", value: false },
        ];
    }

    /** Whether anything other than the Active/Inactive split is narrowing the list. */
    get hasNarrowingFilters() {
        return !!this.query.trim() || this.onlineOnly;
    }

    /**
     * Names the reason the list is empty. "No inactive users found." reads as a statement about the
     * roster, so showing it when the search box or the Online-only switch is what emptied the list
     * misattributes the cause -- there may be plenty of inactive users, just none matching.
     */
    get emptyUsersMessage() {
        if (!this.users.length) return "No users found.";

        const view = this.showActiveUsers ? "active" : "inactive";
        return this.hasNarrowingFilters
            ? `No ${view} users match the current filters.`
            : `No ${view} users found.`;
    }

    selectUser(user: User) {
        this.selectedUser = Object.assign(new User(), structuredClone(toRaw(user)));
        this.$bvModal.show("user-modal");
    }

    protected isWorkingTeamsLoading = false;

    @Watch("selectedUser.teams")
    async getWorkingTeams() {
        try {
            this.workingTeams = [];

            if (!this.selectedUser?.teams) return;

            this.isWorkingTeamsLoading = true;

            const teamPromises = this.selectedUser.teams.map(async (t: Team) => {
                const resp = await this.$http.get<Team>(`/user/teams/${t.id}`);
                return resp.data;
            });

            this.workingTeams = await Promise.all(teamPromises);
            this.isWorkingTeamsLoading = false;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async handleOk(bvModalEvent: any) {
        bvModalEvent.preventDefault();

        try {
            const response = await axios.post(`/User/SaveCallback`, this.selectedUser);
            const responseUser = response.data;
            this.users = this.users.map(u => u.userName == responseUser.userName ? responseUser : u).map(u => new User(u));
            this.$nextTick(() => {
                this.selectedUser = new User();
                this.$bvModal.hide("user-modal");
            });
            this.$notification(NotificationOptions.successSaveNotificationPreset("User"));
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    get userCanEdit() {
        return this.$permissions.some((p) => p.groupName === "Admin" && p.name === "Users");
    }

    get canEnrollPositiveId() {
        return this.$permissions.some((p) => p.groupName === "Compliance" && p.name === "Positive ID Enrollment");
    }

    get canToggleUserActive() {
        return this.$permissions.some((p) => p.permissionCodeName === "Admin/Deactivate Users");
    }

    get isSelectedUserSelf() {
        return this.selectedUser?.userName?.toLowerCase() === this.$user.userName?.toLowerCase();
    }

    /** Renders the "on <date> by <who>" tail shared by the inactive cards and the modal banner. */
    describeDeactivation(user: User) {
        const on = user.deactivatedDate ? ` on ${formatDateAndTime(user.deactivatedDate)}` : "";
        const by = user.deactivatedBy ? ` by ${user.deactivatedBy}` : "";
        return `${on}${by}`.trim() || "at an unrecorded time";
    }

    async confirmDeactivateUser() {
        if (!this.selectedUser?.userName) return;

        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Deactivate ${this.selectedUser.toString()}? They will be hidden from assignment dropdowns `
            + `and moved to the Inactive list. They can still sign in, and doing so reactivates them.`,
            {
                title: "Deactivate User",
                okVariant: "danger",
                okTitle: "Deactivate",
                cancelTitle: "Cancel",
            });

        if (confirmed) await this.setUserActive(false);
    }

    async confirmReactivateUser() {
        if (!this.selectedUser?.userName) return;

        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Reactivate ${this.selectedUser.toString()}? They will appear in assignment dropdowns again.`,
            {
                title: "Reactivate User",
                okVariant: "success",
                okTitle: "Reactivate",
                cancelTitle: "Cancel",
            });

        if (confirmed) await this.setUserActive(true);
    }

    private async setUserActive(active: boolean) {
        const userName = this.selectedUser?.userName;
        if (!userName) return;

        // Only the toggle itself is inside the try: a failure here means nothing changed on the
        // server, which is a different message from a failure to re-read the list.
        try {
            await axios.post(`/User/${encodeURIComponent(userName)}/${active ? "reactivate" : "deactivate"}`);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            return;
        }

        this.$bvModal.hide("user-modal");
        this.selectedUser = null;

        // Refetched rather than patched in place so the Active/Inactive counts and the deactivation
        // stamp come from the server that wrote them. fetchAllUsers reports failure by return value
        // rather than throwing, so it has to be checked: the toggle succeeded, but flipping the
        // view over a stale list would land the operator on a list still showing the user in the
        // state they just left, under a success toast claiming otherwise.
        if (!await this.fetchAllUsers(false)) {
            this.$notification(NotificationOptions.warning(
                `User ${active ? "reactivated" : "deactivated"}, but the list could not be refreshed. `
                + `Reload the page to see the current roster.`));
            return;
        }

        this.$notification(NotificationOptions.success(active ? "User reactivated" : "User deactivated"));
        // Land the user on the list that now holds the person they just moved.
        this.showActiveUsers = active;
    }

    removeTeam(team: Team) {
        this.selectedUser!.teams = this.selectedUser!.teams.filter(t => t !== team);
    }

    @Watch("teamToAdd")
    addTeam() {
        if (!this.teamToAdd?.id) return;
        this.selectedUser!.teams.push(this.teamToAdd);
        this.teamToAdd = new Team();
    }

    teamAddSearchResultFilter(teams: Team[]) {
        return teams.filter(t => !this.selectedUser!.teams.find(ut => ut.id === t.id));
    }

    get teamsFields() {
        return [
            { key: 'teams', class: 'w-100' },
            { key: 'actions', label: '', class: 'actions-col' }
        ];
    }

    get effectivePermissions() {
        const permissions: Permission[] = [];

        this.workingTeams.forEach(t => {
            t.permissions.forEach(p => {
                if (permissions.find(i => i.id === p.id)) return;
                permissions.push(p);
            })
        });

        return permissions;
    }

    getAppliedPermissionsCountInGroup(groupName?: string) {
        if (!groupName) return this.filteredEffectivePermissions.length;
        return this.filteredEffectivePermissions.filter(p => p.groupName === groupName).length;
    }

    get effectivePermissionGroups() {
        const groups: string[] = [];

        this.effectivePermissions.forEach(ap => {
            if (groups.includes(ap.groupName)) return;
            groups.push(ap.groupName);
        })

        return groups.toSorted();
    }

    get filteredEffectivePermissions() {
        return this.effectivePermissions.filter(ap => {
            if (!this.permissionsFilterByGroup) return true;
            return ap.groupName === this.permissionsFilterByGroup;
        }).filter(ap => {
            if (!this.permissionFilterQuery?.trim()) return true;
            return ap.name.toLowerCase().includes(this.permissionFilterQuery.toLowerCase());
        });
    }

    get filteredEffectivePermissionsGroups() {
        const groups: string[] = [];

        this.filteredEffectivePermissions.forEach(ap => {
            if (groups.includes(ap.groupName)) return;
            groups.push(ap.groupName);
        })

        return groups.toSorted();
    }

    getEffectivePermissionsForGroup(groupName: string) {
        return this.filteredEffectivePermissions
            .filter(ap => ap.groupName === groupName)
            .toSorted((a, b) => a.name.localeCompare(b.name));
    }

    getTeamsForPermission(permission: Permission) {
        return this.workingTeams.filter(t => t.permissions?.some(p => p.id === permission.id));
    }

    addLicense() {
        this.selectedUserCompliance = {
            active: true,
            state: "",
            licenseNumber: ""
        };
        this.selectedUserComplianceIndex = null;
    }

    saveLicense() {
        const index = this.selectedUserComplianceIndex;
        if (index === null) {
            this.selectedUser!.licenses.push(this.selectedUserCompliance!);
        } else {
            this.selectedUser!.licenses[index] = this.selectedUserCompliance!;
        }
    }

    editLicense(license: UserComplianceLicense, index: number) {
        this.selectedUserCompliance = structuredClone(license);
        this.selectedUserComplianceIndex = index;
    }
}
</script>

<style scoped>
form {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
}

.users-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 330px), 1fr));
    gap: 10px;
}

.online-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #adb5bd;
}

.online-dot.online {
    background-color: #28a745;
}

.permissions {
    height: calc(100vh - 345px);
}

.permissions .groups {
    min-width: 280px;
    max-width: 280px;
    overflow-y: auto;
}

.permissions .groups .list-group-item {
    padding-top: 5px;
    padding-bottom: 5px;
}

.permissions .flex-fill {
    overflow-y: auto;
}

.permissions .flex-fill h6 {
    position: sticky;
    background: white;
    top: 0;
    z-index: 100;
    border-bottom: 1px solid black;
}

.permissions .flex-fill h6 ::v-deep(label) {
    line-height: 1.8;
}

.permissions .checkmarks {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 5px;
}

.permissions .groups input {
    position: sticky;
    top: 0;
    z-index: 100;
    background: white;
}

.state-licenses {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
    gap: 8px;

    .card {
        cursor: pointer;
    }
}
</style>
